<template>
  <DModal :show-close="false" :isBgOverlay="true" :placeInTop="true" :autoFocusField="false" @close="$emit('close')">
    <div class="d-modal" slot="insertComponent">
      <div class="d-modal__header">
        <h2>Opret klasse på tur</h2>
      </div>
      <div class="d-modal__content">
        <div class="d-group">
          <label class="d-label">Vælg klasser</label>
          <DSelectCals :items="klassCalendars"
                       :itemsSelected="selectedKlasses"
                       :multiple="true"
                       :placeholder="$t('vue2.dselect.placeholder_calendars')"
                       :showCalendarYear="true"
                       @update="setSelectedKlasses">
          </DSelectCals>
        </div>
        <template v-if="selectedKlasses.length > 0">
          <ClassOnTripSubject
            :schoolId="schoolId"
            :calendarId="selectedKlasses[0]['id']"
            :date="date"
            :selectedSubject="selectedSubject"
            @setSubject="setSelectedSubject">
          </ClassOnTripSubject>
        </template>
        <div class="d-group">
          <label class="d-label">Personale</label>
          <DSelectCals :items="userCalendars"
                       :itemsSelected="selectedUsers"
                       :multiple="true"
                       :placeholder="$t('vue2.dselect.placeholder_calendars')"
                       @update="setSelectedUsers">
          </DSelectCals>
        </div>
        <div class="d-group">
          <label class="d-label">Dato</label>
          <DateSelector
            :date="date"
            @changed="setDate">
          </DateSelector>
        </div>
        <div class="time-changer__group">
          <label class="d-label">Tid</label>
          <TimeChanger
            :showIcon="true"
            :start="startTime"
            :end="endTime"
            :isFiveMinutesInterval="false"
            @updateStart="setStartTime"
            @updateEnd="setEndTime">
          </TimeChanger>
        </div>
      </div>
      <div class="d-modal__buttons d-modal__buttons__padding" style="padding: 1rem 3rem;">
        <button class="do-bttn" @click="$emit('close')">{{ $t('vue2.common.cancel') }}</button>
        <button class="do-bttn do-bttn--submit do-bttn--submit--disabled"
                v-if="!isValidated">
          {{ $t('vue2.common.create') }}
        </button>
        <button class="do-bttn do-bttn--submit" :class="{'do-bttn--submit--loading': isSubmitting}" v-else
                @click="submit">{{ $t('vue2.common.create') }}
        </button>
      </div>
    </div>
  </DModal>
</template>

<script>
import docendo from '../../../docendo'
import DModal from '../../common/DModal'
import eventbus from '../../../eventbus'
import DSelectCals from '../../DSelect/DSelectCals.vue'
import {mapGetters} from 'vuex'
import moment from 'moment'
import DateSelector from '../../datepicker/DateSelector.vue'
import TimeChanger from '../../TimeChanger.vue'
import ClassOnTripSubject from './ClassOnTripSubject.vue'

export default {
  components: {ClassOnTripSubject, DateSelector, TimeChanger, DSelectCals, DModal},
  props: {
    initDate: {
      default: () => moment()
    }
  },
  data () {
    return {
      date: moment(),
      startTime: moment().set({'hour': 8, 'minutes': 0}),
      endTime: moment().set({'hour': 16, 'minutes': 0}),
      selectedKlasses: [],
      selectedSubject: null,
      selectedUsers: [],
      klassEvents: [],
      isSubmitting: false
    }
  },
  computed: {
    ...mapGetters([
      'klassCalendars',
      'userCalendars',
      'schoolId'
    ]),
    isValidated () {
      return this.selectedKlasses.length > 0 && this.selectedSubject && this.date && this.startTime && this.endTime
    },
    dbEventStart () {
      const date = moment(this.date).format('YYYY-MM-DD')
      const time = this.startTime.locale('LT')
      return moment(date).set({
        hour: time.get('hour'),
        minutes: time.get('minutes')
      })
    },
    dbEventEnd () {
      const date = moment(this.date).format('YYYY-MM-DD')
      const time = this.endTime.locale('LT')
      return moment(date).set({
        hour: time.get('hour'),
        minutes: time.get('minutes')
      })
    }
  },
  methods: {
    setSelectedSubject (subject) {
      this.selectedSubject = subject
    },
    setDate (payload) {
      this.date = payload
    },
    setStartTime (payload) {
      this.startTime = payload
    },
    setEndTime (payload) {
      this.endTime = payload
    },
    setSelectedKlasses (val) {
      this.selectedKlasses = val
      if (this.selectedSubject) {
        const subjectCalendarId = this.selectedSubject.calendar_id
        const stillHasSubjectCalendar = val.some(klass => klass.id === subjectCalendarId)
        if (!stillHasSubjectCalendar) {
          this.setSelectedSubject(null)
        }
      } else if (val.length === 0) {
        this.setSelectedSubject(null)
      }
    },
    setSelectedUsers (val) {
      this.selectedUsers = val
    },
    submit () {
      this.isSubmitting = true
      this.readKlassEvents(() => {
        this.createMainEvent(createdEvent => {
          this.cancelOverlappingEvents(createdEvent, () => {
            this.isSubmitting = false
            this.showToast(this.$t('vue2.create_activity.created'), true)
            this.$emit('onCreatedEvents')
            this.$emit('close')
            this.$store.dispatch('trackEvent', 'substitution_classOnTrip_submit')
          })
        })
      })
    },
    readKlassEvents (callback) {
      const calendarIds = this.selectedKlasses.map(calendar => calendar.id)
      docendo.calendarEvents2(calendarIds, this.dbEventStart, this.dbEventEnd, null, success => {
        this.klassEvents = success && success.length > 0 ? JSON.parse(success) : []
        callback()
      }, failure => {
        this.showToast('Error fetching class events', false)
        this.isSubmitting = false
      })
    },
    createMainEvent (callback) {
      const schoolId = this.selectedSubject.school_id || this.schoolId
      const calendarId = this.selectedSubject.calendar_id
      const selectedKlassIds = this.selectedKlasses.map(calendar => calendar.id)
      const selectedUserIds = this.selectedUsers.map(calendar => calendar.id)
      const calendarIds = [this.selectedSubject.calendar_id, ...selectedKlassIds, ...selectedUserIds]
        .filter((id, index, ids) => id && ids.indexOf(id) === index)
      const subjectId = this.selectedSubject.id
      const internalNote = ''

      docendo.createEvent(
        schoolId,
        calendarId,
        calendarIds,
        subjectId,
        this.dbEventStart,
        this.dbEventEnd,
        '',
        '',
        '',
        internalNote,
        true,
        data => {
          if (data && data.status === 'error') {
            this.showToast(data.message, false)
            this.isSubmitting = false
            return
          }
          let createdEvent = data
          if (typeof data === 'string') {
            createdEvent = JSON.parse(data)
          }
          callback(createdEvent)
        },
        failure => {
          this.showToast('Something went wrong', false)
          this.isSubmitting = false
        }
      )
    },
    cancelOverlappingEvents (createdEvent, callback) {
      const createdEventId = createdEvent && (createdEvent.id || createdEvent.event_id)
      const tripKlassIds = this.selectedKlasses.map(calendar => calendar.id)
      const candidates = this.klassEvents.filter(ev => {
        const notAlreadyCanceled = ev && ev.canceled !== true
        const notAlreadyAway = ev && ev.away !== true
        const notSelf = !createdEventId || (ev.event_id !== createdEventId && ev.id !== createdEventId)
        // Undlad fælles events (fx valgfag) hvor andre klasser også er på
        const hasOtherKlasses = this.eventHasOtherKlasses(ev, tripKlassIds)
        return notAlreadyCanceled && notAlreadyAway && notSelf && !hasOtherKlasses
      })

      if (candidates.length === 0) {
        callback()
        return
      }

      const schoolId = this.selectedSubject.school_id || this.schoolId
      const updates = candidates.map(ev => {
        return {event_id: ev.event_id || ev.id, canceled: true}
      })

      docendo.updateEvents(
        schoolId,
        updates,
        () => {
          callback()
        },
        () => {
          this.showToast('Error canceling overlapping events', false)
          this.isSubmitting = false
        }
      )
    },
    eventHasOtherKlasses (ev, tripKlassIds) {
      const calendars = (ev && (ev.calendars || ev.calendar_events)) || []
      const klassIds = calendars
        .filter(c => c && c.calendar_type === 'klass')
        .map(c => c.calendar_id)
      if (klassIds.length === 0) return false
      return klassIds.some(id => tripKlassIds.indexOf(id) === -1)
    },
    showToast (message, success) {
      eventbus.toast = {text: message, success}
    }
  },
  beforeMount () {
    this.date = this.initDate
  }
}
</script>

<style scoped>
.d-modal {
  width: 550px;
  border-radius: 16px;
  position: relative;
}

.d-modal__header {
  margin-bottom: 0;
}

.d-modal__buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.d-modal__content {
  padding: 1rem 3rem;
  overflow-y: auto;
  max-height: calc(100vh - 2rem - 52px - 60px);
}

.d-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.time-changer__group {
  margin-bottom: 1rem;
}
</style>



// WEBPACK FOOTER //
// ClassOnTrip.vue?30e7d523